import {
  DATE_PRESET_LABEL,
  DATE_PRESETS,
  columnLabels,
  datePresetRange,
  gameTypeChoices,
  historyRequests,
  platformChoices,
  presentHistoryPage,
  profitClass,
} from './jitoBettingRecordsView.js';

export function createJitoBettingRecordsPage({
  useState,
  useEffect,
  jsx,
  jsxs,
  fetchGames,
  fetchSports,
  fetchFilters,
}) {
  function ClipboardEmpty() {
    return jsxs('div', {
      className: 'jbr-empty',
      children: [
        jsxs('svg', {
          className: 'jbr-empty-icon',
          viewBox: '0 0 64 64',
          'aria-hidden': 'true',
          children: [
            jsx('rect', {
              x: '18', y: '12', width: '28', height: '40', rx: '3',
              fill: '#1b315c', stroke: '#aebbd0', strokeWidth: '2',
            }, 'a'),
            jsx('rect', {
              x: '24', y: '8', width: '16', height: '8', rx: '2',
              fill: '#07101f', stroke: '#aebbd0', strokeWidth: '2',
            }, 'b'),
            jsx('path', {
              d: 'M26 28h12M26 36h12M26 44h8',
              fill: 'none', stroke: '#aebbd0', strokeWidth: '2', strokeLinecap: 'round',
            }, 'c'),
          ],
        }),
        jsx('p', { children: 'No Data' }),
      ],
    });
  }

  function FunnelIcon() {
    return jsxs('svg', {
      className: 'jbr-icon',
      viewBox: '0 0 24 24',
      'aria-hidden': 'true',
      children: [
        jsx('path', { d: 'M4 5h16l-6.2 7.4V19l-3.6-2v-4.6L4 5Z', fill: 'currentColor' }),
      ],
    });
  }

  function Chip({ label, selected, onSelect }) {
    return jsx('button', {
      type: 'button',
      className: selected ? 'jbr-chip is-on' : 'jbr-chip',
      'aria-pressed': selected,
      onClick: onSelect,
      children: label,
    });
  }

  function JitoBettingRecordsPage({ onClose }) {
    const [tab, setTab] = useState('settled');
    const [filters, setFilters] = useState({ categories: [], providers: [] });
    const [applied, setApplied] = useState(() => ({
      provider: 'All',
      gameType: 'All',
      ...datePresetRange('last7'),
    }));
    const [draft, setDraft] = useState(applied);
    const [filterOpen, setFilterOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [expanded, setExpanded] = useState('');
    const [reload, setReload] = useState(0);
    const [sportsRecords, setSportsRecords] = useState([]);
    const [gameRecords, setGameRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sportsError, setSportsError] = useState('');

    useEffect(() => {
      let cancelled = false;
      Promise.resolve()
        .then(() => fetchFilters())
        .then((data) => {
          if (cancelled || !data) return;
          setFilters({
            categories: Array.isArray(data.categories) ? data.categories : [],
            providers: Array.isArray(data.providers) ? data.providers : [],
          });
        })
        .catch(() => {});
      return () => {
        cancelled = true;
      };
    }, []);

    useEffect(() => {
      let cancelled = false;
      const request = historyRequests({ ...applied, tab });
      setLoading(true);
      setError('');
      setSportsError('');
      Promise.allSettled([
        fetchSports(request.sports),
        fetchGames(request.games),
      ]).then(([sportsResult, gameResult]) => {
        if (cancelled) return;
        if (sportsResult.status === 'fulfilled') {
          const pageData = sportsResult.value || {};
          setSportsRecords(Array.isArray(pageData.records) ? pageData.records : []);
          setSportsError('');
        } else {
          setSportsRecords([]);
          setSportsError(sportsResult.reason?.message || 'Unable to load 9Wicket betting history. Please try again.');
        }
        if (gameResult.status === 'fulfilled') {
          const pageData = gameResult.value || {};
          setGameRecords(Array.isArray(pageData.records) ? pageData.records : []);
          setError('');
        } else {
          setGameRecords([]);
          setError(gameResult.reason?.message || 'Failed to load history');
        }
        setLoading(false);
      });
      return () => {
        cancelled = true;
      };
    }, [applied.from, applied.to, applied.gameType, applied.provider, tab, reload]);

    const view = presentHistoryPage(sportsRecords, gameRecords, { ...applied, tab, page });
    const columns = columnLabels(tab);
    const typeChips = gameTypeChoices(filters, sportsRecords, gameRecords);
    const providerChips = platformChoices(filters, sportsRecords.concat(gameRecords));

    const openFilter = () => {
      setDraft(applied);
      setFilterOpen(true);
    };
    const confirmFilter = () => {
      setApplied(draft);
      setPage(1);
      setExpanded('');
      setFilterOpen(false);
    };
    const closePage = () => {
      if (typeof onClose === 'function') onClose();
    };

    return jsxs('div', {
      className: 'jbr-page',
      'aria-label': 'Betting Records',
      children: [
        jsxs('header', {
          className: 'jbr-header',
          children: [
            jsx('h1', { className: 'jbr-title', children: 'Betting Records' }),
            jsx('button', {
              type: 'button',
              className: 'jbr-close',
              'aria-label': 'Close',
              onClick: closePage,
              children: jsx('svg', {
                className: 'jbr-icon',
                viewBox: '0 0 24 24',
                'aria-hidden': 'true',
                children: jsx('path', {
                  d: 'M6 6l12 12M18 6L6 18',
                  fill: 'none',
                  stroke: 'currentColor',
                  strokeWidth: '2',
                  strokeLinecap: 'round',
                }),
              }),
            }),
          ],
        }),
        jsxs('div', {
          className: 'jbr-tabs',
          role: 'tablist',
          children: [
            jsx('button', {
              type: 'button',
              role: 'tab',
              'aria-selected': tab === 'settled',
              className: tab === 'settled' ? 'jbr-tab is-on' : 'jbr-tab',
              onClick: () => {
                setTab('settled');
                setPage(1);
                setExpanded('');
              },
              children: 'Settled',
            }),
            jsx('button', {
              type: 'button',
              role: 'tab',
              'aria-selected': tab === 'unsettled',
              className: tab === 'unsettled' ? 'jbr-tab is-on' : 'jbr-tab',
              onClick: () => {
                setTab('unsettled');
                setPage(1);
                setExpanded('');
              },
              children: 'Unsettled',
            }),
          ],
        }),
        jsxs('div', {
          className: 'jbr-toolbar',
          children: [
            jsx('span', {
              className: 'jbr-date-pill',
              children: DATE_PRESET_LABEL[applied.preset] || DATE_PRESET_LABEL.last7,
            }),
            jsx('button', {
              type: 'button',
              className: 'jbr-filter-btn',
              'aria-label': 'Betting Record Filter',
              onClick: openFilter,
              children: jsx(FunnelIcon, {}),
            }),
          ],
        }),
        jsxs('div', {
          className: 'jbr-cols',
          children: columns.map((label) => jsx('span', { children: label }, label)),
        }),
        error
          ? jsxs('p', {
            className: 'jbr-error',
            children: [
              error,
              ' ',
              jsx('button', {
                type: 'button',
                onClick: () => setReload((n) => n + 1),
                children: 'Retry',
              }),
            ],
          })
          : null,
        sportsError && !error
          ? jsx('p', { className: 'jbr-sports-error', children: sportsError })
          : null,
        loading
          ? jsx('p', { className: 'jbr-loading account-local-status', children: 'Loading…' })
          : error
            ? null
            : view.rows.length
              ? jsx('ul', {
                className: 'jbr-list',
                children: view.rows.map((row) => {
                  const open = expanded === row.key;
                  return jsxs('li', {
                    className: open ? 'jbr-row is-open' : 'jbr-row',
                    children: [
                      jsxs('button', {
                        type: 'button',
                        className: 'jbr-row-main',
                        'aria-expanded': open,
                        onClick: () => setExpanded(open ? '' : row.key),
                        children: [
                          row.cells.map((cell, idx) => jsx('span', {
                            className: idx === 3 && tab === 'settled' ? `jbr-cell ${profitClass(row.profitLoss)}` : 'jbr-cell',
                            children: cell,
                          }, idx)),
                          jsx('span', { className: 'jbr-chevron', 'aria-hidden': 'true' }),
                        ],
                      }),
                      open
                        ? jsx('dl', {
                          className: 'jbr-details',
                          children: row.details.map(([label, value]) => jsxs('div', {
                            children: [
                              jsx('dt', { children: label }),
                              jsx('dd', {
                                className: label === 'Profit / Loss' ? profitClass(row.profitLoss) : '',
                                children: value,
                              }),
                            ],
                          }, label)),
                        })
                        : null,
                    ],
                  }, row.key);
                }),
              })
              : jsx(ClipboardEmpty, {}),
        !loading && (view.page > 1 || view.page < view.totalPages)
          ? jsxs('div', {
            className: 'jbr-pager',
            children: [
              jsx('button', {
                type: 'button',
                disabled: view.page <= 1,
                onClick: () => {
                  setPage((n) => Math.max(1, n - 1));
                  setExpanded('');
                },
                children: 'Previous',
              }),
              jsxs('span', { children: ['Page ', view.page] }),
              jsx('button', {
                type: 'button',
                disabled: view.page >= view.totalPages,
                onClick: () => {
                  setPage((n) => n + 1);
                  setExpanded('');
                },
                children: 'Next',
              }),
            ],
          })
          : null,
        filterOpen
          ? jsxs('div', {
            className: 'jbr-sheet',
            role: 'dialog',
            'aria-label': 'Betting Record Filter',
            children: [
              jsxs('header', {
                className: 'jbr-sheet-head',
                children: [
                  jsx('button', {
                    type: 'button',
                    className: 'jbr-back',
                    'aria-label': 'Back',
                    onClick: () => setFilterOpen(false),
                    children: '‹',
                  }),
                  jsx('h2', { children: 'Betting Record Filter' }),
                ],
              }),
              jsxs('div', {
                className: 'jbr-sheet-body',
                children: [
                  jsx('h3', { children: 'Game Type' }),
                  jsxs('div', {
                    className: 'jbr-chips',
                    children: [
                      jsx(Chip, {
                        label: 'All',
                        selected: draft.gameType === 'All',
                        onSelect: () => setDraft((prev) => ({ ...prev, gameType: 'All' })),
                      }),
                      ...typeChips.map((item) => jsx(Chip, {
                        label: item.label,
                        selected: draft.gameType === item.value,
                        onSelect: () => setDraft((prev) => ({ ...prev, gameType: item.value })),
                      }, item.value)),
                    ],
                  }),
                  jsx('h3', { children: 'Platform' }),
                  jsxs('div', {
                    className: 'jbr-chips',
                    children: [
                      jsx(Chip, {
                        label: 'All',
                        selected: draft.provider === 'All',
                        onSelect: () => setDraft((prev) => ({ ...prev, provider: 'All' })),
                      }),
                      ...providerChips.map((item) => jsx(Chip, {
                        label: item.label,
                        selected: draft.provider === item.value,
                        onSelect: () => setDraft((prev) => ({ ...prev, provider: item.value })),
                      }, item.value)),
                    ],
                  }),
                  jsx('h3', { children: 'Date' }),
                  jsx('div', {
                    className: 'jbr-chips jbr-chips-date',
                    children: DATE_PRESETS.map((key) => jsx(Chip, {
                      label: DATE_PRESET_LABEL[key],
                      selected: draft.preset === key,
                      onSelect: () => setDraft((prev) => ({ ...prev, ...datePresetRange(key) })),
                    }, key)),
                  }),
                ],
              }),
              jsx('button', {
                type: 'button',
                className: 'jbr-confirm',
                onClick: confirmFilter,
                children: 'Confirm',
              }),
            ],
          })
          : null,
      ],
    });
  }

  return JitoBettingRecordsPage;
}
